/**
 * Phỏng vấn khai báo — phần thuần của `KhaiBaoTab.tsx` native.
 *
 * Gợi ý KHÔNG BAO GIỜ tự thành lời khai: ô điền sẵn mang nhãn "gợi ý · chưa duyệt", và nút Lưu không đếm nó cho tới khi
 * có người bấm duyệt. Bỏ bước ấy thì ô trống trước đây nói THẬT ("chưa ai khai") sẽ nói DỐI bằng một phép đoán từ tên cột.
 */
import type { CauHoiKhai, DeXuatKhai, TraLoiPhongVan } from '../types.ts';

/**
 * Hai mức GÂY HẠI gộp lại làm bộ lọc mặc định. Đo trên nguồn `hera` (MES, 35 bảng): 763 câu, 600 ở mức `kho_doc`. Mở màn
 * ra 763 dòng thì người ta bỏ giữa chừng — và đúng 55 câu `sai_am_tham` nguy hiểm nhất không ai trả lời. Không giấu gì:
 * chip `kho_doc` vẫn đứng đó kèm số của nó.
 */
export const DANG_LAM_TRUOC = 'sai_am_tham,bi_tu_choi';

/** Bốn chip lọc theo hậu quả, nặng dần, kèm tông. */
export const HAU_QUA = [
  [DANG_LAM_TRUOC, 'danger'],
  ['sai_am_tham', 'danger'],
  ['bi_tu_choi', 'warning'],
  ['kho_doc', 'neutral'],
] as const;

export type DeXuatBoNho = Record<string, { giaTri: string; viSao: string; cap?: Record<string, string> }>;

/**
 * Gộp đề xuất (từ tệp trả lời) vào `goiY`. KHÔNG đè gợi ý nguồn `ho_so` — bằng chứng đo từ dữ liệu khách thắng lời kể của
 * tài liệu; gợi ý `tu_dien` (đoán theo tên cột) thì đè được.
 */
export function gopDeXuat(q: CauHoiKhai, deXuat: DeXuatBoNho): CauHoiKhai {
  const d = deXuat[q.ma];
  if (!d || (q.goiY && q.goiY.nguon === 'ho_so')) return q;
  return { ...q, goiY: { giaTri: d.giaTri, nguon: 'tep_tra_loi', viSao: d.viSao, doTin: 'vua', cap: d.cap } };
}

/** Câu trùng mã (một cột ở nhiều khuôn câu hỏi) gộp từ KHO mọi bảng đã mở, không từ phản hồi cuối. */
export function gopKho(kho: Record<string, CauHoiKhai[]>): CauHoiKhai[] {
  const thay = new Map<string, CauHoiKhai>();
  for (const cs of Object.values(kho)) for (const q of cs) if (!thay.has(q.ma)) thay.set(q.ma, q);
  return [...thay.values()];
}

/**
 * Câu ĐÃ TRẢ LỜI = gõ tay, HOẶC duyệt một gợi ý. Câu nghĩa mã trả về ÁNH XẠ: nghĩa gõ tay đè nghĩa được gợi ý (chỉ khi đã
 * duyệt), bỏ mã còn để trống.
 */
export function dungTraLoi(
  tatCa: CauHoiKhai[],
  dap: Record<string, string>,
  dapCap: Record<string, Record<string, string>>,
  duyet: Record<string, boolean>,
): TraLoiPhongVan[] {
  const ra: TraLoiPhongVan[] = [];
  for (const q of tatCa) {
    if (q.nhieuLuaChon) {
      const nen = duyet[q.ma] ? (q.goiY?.cap ?? {}) : {};
      const gop = { ...nen, ...(dapCap[q.ma] ?? {}) };
      const sach = Object.fromEntries(
        Object.entries(gop)
          .map(([k, v]) => [k, v.trim()] as const)
          .filter(([, v]) => v !== ''),
      );
      if (Object.keys(sach).length > 0) ra.push({ ma: q.ma, cap: sach });
      continue;
    }
    const tho = dap[q.ma]?.trim();
    if (tho) ra.push({ ma: q.ma, giaTri: tho });
    else if (duyet[q.ma] && q.goiY) ra.push({ ma: q.ma, giaTri: q.goiY.giaTri });
  }
  return ra;
}

/**
 * Gợi ý nào ĐƯỢC duyệt hàng loạt. Hai cổng: ① `doTin !== 'thap'` — mọi gợi ý suy theo TÊN cột là `thap`, và duyệt hàng
 * loạt là duyệt KHÔNG ĐỌC; ② `hauQua !== 'sai_am_tham'` — sai ở "mỗi dòng là gì", nghĩa mã, phạm vi bảng, thuật ngữ cho ra
 * một con số có thật trả lời sai câu hỏi, và không phép kiểm nào bắt được.
 */
export function duyetHangLoatDuoc(q: CauHoiKhai): boolean {
  return q.goiY?.doTin !== 'thap' && q.hauQua !== 'sai_am_tham';
}

/** Câu chưa duyệt, chưa gõ, có gợi ý và qua được hai cổng trên. */
export function duyetDuocCua(
  ds: CauHoiKhai[],
  dap: Record<string, string>,
  dapCap: Record<string, Record<string, string>>,
  duyet: Record<string, boolean>,
): CauHoiKhai[] {
  const daGoCap = (q: CauHoiKhai) => Object.values(dapCap[q.ma] ?? {}).some((v) => v.trim() !== '');
  return ds.filter(
    (q) =>
      duyetHangLoatDuoc(q) &&
      (q.nhieuLuaChon
        ? Object.keys(q.goiY?.cap ?? {}).length > 0 && !duyet[q.ma] && !daGoCap(q)
        : Boolean(q.goiY) && !duyet[q.ma] && !dap[q.ma]?.trim()),
  );
}

/**
 * Câu CHỌN MỘT mà tệp trả lời được thì bấm sẵn nút ấy, y như người dùng tự bấm. Không đè thứ người dùng đã gõ, và không
 * đè gợi ý `ho_so` khác giá trị. Câu tự do và câu nghĩa mã vẫn là gợi ý chờ duyệt.
 */
export function chonSanLuaChon(
  goc: CauHoiKhai[],
  ds: DeXuatKhai[],
  dap: Record<string, string>,
): Record<string, string> {
  const theoMa = new Map(goc.map((q) => [q.ma, q]));
  const chon: Record<string, string> = {};
  for (const d of ds) {
    const q = theoMa.get(d.ma);
    if (!q || q.nhieuLuaChon || q.traLoiTuDo || dap[d.ma]?.trim()) continue;
    if (!q.luaChon.some((c) => c.giaTri === d.giaTri)) continue;
    if (q.goiY?.nguon === 'ho_so' && q.goiY.giaTri !== d.giaTri) continue;
    chon[d.ma] = d.giaTri;
  }
  return chon;
}

/** Gom câu theo bảng — cho kho câu đã tải. */
export function theoBangCua(ds: CauHoiKhai[]): Record<string, CauHoiKhai[]> {
  const moi: Record<string, CauHoiKhai[]> = {};
  for (const q of ds) (moi[q.bang || ''] ??= []).push(q);
  return moi;
}

/** Số của chip: chip gộp đếm bằng TỔNG hai mức con (`theoHauQua` khoá theo từng mức). */
export function demChip(k: string, theoHauQua: Record<string, number>, tatCa: CauHoiKhai[]): number {
  return k.includes(',')
    ? k.split(',').reduce((t, x) => t + (theoHauQua[x] ?? 0), 0)
    : (theoHauQua[k] ?? tatCa.filter((q) => q.hauQua === k).length);
}
